import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useStacklineTheme } from '@stackline/ui';
import {
  useMetricFormatter,
  useComparisonPeriod,
  ComparisonPeriod,
  useWeekRange,
  WeekRange,
  useQueryParamValue
} from 'src/utils/Hooks';
import ReactDOMServer from 'react-dom/server';
import HoverTooltip from '../HoverTooltip/HoverTooltip';
import { ValueOf } from 'sl-api-connector';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';
import { getStartAndEndOfWeek, timestampToDayId, timestampToWeekId, weekIdToTimestamp } from 'src/utils/dateUtils';
import moment from 'moment-timezone';
import _merge from 'lodash/merge';
import _get from 'lodash/get';
import { BEACON_TABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import { StacklineDate } from 'stackline-datetime';
import colors from 'src/utils/colors';

type SplineChartHighchartsOptions = Omit<Highcharts.Options, 'series'> & {
  series: Partial<Highcharts.SeriesOptionsType>[];
};

export type SplineChartOptionsOverride = Partial<SplineChartHighchartsOptions>;

interface RenderCustomTooltipParams {
  chartInstance: Highcharts.TooltipFormatterContextObject;
  primaryPoint: Highcharts.Point;
  secondaryPoint: Highcharts.Point;
  percentChange: number | undefined;
  weekId: number;
  dayId?: number;
  metricType: ValueOf<typeof METRICTYPE>;
}

const getWeekId = (timeStamp: number) => {
  return timestampToWeekId(timeStamp) % 100;
};

export interface SplineChartProps {
  byDayId?: boolean;
  primaryData: number[][];
  secondaryData?: number[][];
  /**
   * Optional series to display with the primary
   * data as a forecast, which will appear as a
   * dashed line.
   */
  forecastedData?: number[][];
  metricType: ValueOf<typeof METRICTYPE>;
  preview?: 'large' | 'small' | { width: number; height: number };
  width?: number | string;
  chartOptionsOverride?: SplineChartOptionsOverride;
  removeSpacing?: boolean;
  NoDataPlaceholder?: React.ReactNode;
  /**
   * By default we use some logic to filter which points should appear
   * in the tooltip. This is useful for when you want to override that
   * logic, e.g. to show all points.
   */
  buildPointsForTooltip?: (
    points: Highcharts.TooltipFormatterContextObject[]
  ) => Highcharts.TooltipFormatterContextObject[];

  /**
   * Optionally override the default tooltip rendering logic.
   */
  renderCustomTooltip?: (params: RenderCustomTooltipParams) => string;
  modifySeries?: (series: Highcharts.SeriesOptionsType[]) => Highcharts.SeriesOptionsType[];
}

export const getFormattedMetricDateLabel = (weekId: number, dayId?: number, priorYear?: boolean): string => {
  // If dayId is provided, use it directly
  if (dayId) {
    const weekDate = moment(dayId, 'YYYYMMDD');
    if (priorYear) {
      return weekDate.subtract(1, 'year').format('M/D');
    }
    return weekDate.format('M/D');
  }

  // Use SDS to get the end date of the week
  const { endDate } = getStartAndEndOfWeek(weekId);
  if (!endDate) {
    return '';
  }

  const weekDate = moment(endDate, 'YYYYMMDD');
  if (priorYear) {
    const year = Math.floor(weekId / 100) - 1;
    const week = Math.min(weekId % 100, StacklineDate.getSDSWeeksInYear(year));
    const endDate = StacklineDate.getEndDateSDS({ year, week });
    return endDate.toFormat('M/d');
  }

  return weekDate.format('M/D');
};

/**
 * Generic container for spline charts.
 */
const SplineChart = ({
  byDayId,
  primaryData,
  secondaryData = [],
  forecastedData,
  metricType,
  preview,
  chartOptionsOverride,
  width,
  removeSpacing = false,
  NoDataPlaceholder,
  buildPointsForTooltip,
  renderCustomTooltip,
  modifySeries
}: SplineChartProps) => {
  const theme = useStacklineTheme();
  const formatMetric = useMetricFormatter();
  const comparisonPeriod = useComparisonPeriod();
  const weekRange = useWeekRange();
  const tab = useQueryParamValue('tab');

  const chartDimensionsMap = useMemo(
    () => ({
      large: {
        width: width || 299,
        height: 85 // top card
      },
      small: {
        width: width || 178,
        height: 85 // middle card
      },
      custom: typeof preview === 'object' ? preview : undefined
    }),
    [width, preview]
  );

  const chartMin = useMemo(() => {
    const allYValues = [
      ...primaryData.map((row) => row?.[1]),
      ...(secondaryData || []).map((row) => row?.[1]),
      ...(forecastedData || []).map((row) => row?.[1])
    ];
    return Math.min(...allYValues);
  }, [forecastedData, primaryData, secondaryData]);

  // Special case, when comparing last week with prior year we want
  // to show the graph for YTD but the legend should just be last week
  const isLastWeekWithPriorYear = useMemo(() => {
    return (
      tab !== 'forecasts' &&
      tab !== BEACON_TABS.ADVERTISING && // We do not want marker dots for advertising chart
      weekRange === WeekRange.LAST_WEEK &&
      comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
    );
  }, [comparisonPeriod, weekRange, tab]);

  const shouldEnableMarketByLastWeekPriorPeriod = useMemo(() => {
    return (
      tab !== 'forecasts' &&
      tab !== BEACON_TABS.ADVERTISING && // We do not want marker dots for advertising chart
      weekRange === WeekRange.LAST_WEEK &&
      comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD
    );
  }, [comparisonPeriod, weekRange, tab]);

  const textStyles: Highcharts.CSSObject = {
    fontFamily: theme.text.fontFamily,
    color: theme.colors.primary,
    fontSize: '12px'
  };

  const seriesMarker = {
    enabled: false,
    symbol: 'circle',
    states: {
      hover: {
        radius: 4,
        lineWidth: 0
      }
    }
  };

  const previewOptions: Partial<Highcharts.Options> = {
    chart: {
      ...(chartDimensionsMap[typeof preview === 'object' ? 'custom' : preview] || {}),
      marginBottom: 10,
      backgroundColor: 'transparent'
    },
    xAxis: {
      visible: false,
      height: 0
    },
    yAxis: [
      {
        visible: false
      }
    ],
    plotOptions: {
      series: {
        lineWidth: 1.5
      }
    }
  };

  const chartSeries = useMemo(() => {
    const primaryWeekIdDot = getWeekId(primaryData[primaryData.length - 1]?.[0]);
    const isAdvertisingChart = byDayId && tab === BEACON_TABS.ADVERTISING;
    const primaryColor = isAdvertisingChart ? colors.stacklineBlue : theme.colors.primary;
    const comparisonColor = isAdvertisingChart ? colors.comparison : theme.colors.casper;

    return [
      {
        type: 'spline',
        data: forecastedData || [],
        dashStyle: 'Dash',
        color: primaryColor,
        marker: seriesMarker,
        enableMouseTracking: true,
        zIndex: 1
      },
      {
        type: 'spline',
        data: primaryData.map((row, index) => ({
          x: row?.[0],
          y: row?.[1],
          marker: {
            enabled:
              shouldEnableMarketByLastWeekPriorPeriod || (isLastWeekWithPriorYear && index === primaryData.length - 1)
          }
        })),
        color: primaryColor,
        marker: seriesMarker,
        zIndex: 1
      },
      {
        type: 'spline',
        data: secondaryData.map((row) => ({
          x: row?.[0],
          y: row?.[1],
          marker: {
            enabled:
              shouldEnableMarketByLastWeekPriorPeriod ||
              (isLastWeekWithPriorYear && getWeekId(row?.[0]) === primaryWeekIdDot)
          }
        })),
        color: comparisonColor,
        marker: seriesMarker
      }
    ].map((series, index) => _merge(series, _get(chartOptionsOverride, ['series', index], {})));
  }, [
    byDayId,
    tab,
    forecastedData,
    primaryData,
    secondaryData,
    theme.colors.primary,
    theme.colors.casper,
    shouldEnableMarketByLastWeekPriorPeriod,
    isLastWeekWithPriorYear,
    chartOptionsOverride,
    seriesMarker
  ]);

  const series = useMemo(() => {
    return modifySeries?.(chartSeries) ?? chartSeries;
  }, [chartSeries, modifySeries]);



  const options: Highcharts.Options = _merge(
    {
      chart: {
        type: 'spline',
        ...(removeSpacing ? { spacing: [0, 0, 0, 0] } : {}),
        spacingLeft: 0
      },
      title: {
        text: ''
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      legend: {
        enabled: false
      },
      tooltip: {
        enabled: true,
        shared: true,
        shadow: false,
        hideDelay: 0, //
        outside: true,
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        useHTML: true,
        formatter() {
          const atForecastIntersection =
            this.points[0].series.index === 0 && this.points[1] && this.points[1].series.index === 1;
          const atPriorPeriodIntersection =
            comparisonPeriod === ComparisonPeriod.PRIOR_PERIOD && this.points.length > 1;

          const [primaryPoint, secondaryPoint] = buildPointsForTooltip
            ? buildPointsForTooltip(this.points)
            : this.points.slice(
              atForecastIntersection || atPriorPeriodIntersection ? 1 : 0 // Remove forecast point (first point) if we're looking at intersection of all three, because forecast and actual data are connected
            );

          const percentChange = secondaryPoint ? calculatePercentChange(primaryPoint.y, secondaryPoint.y) : undefined;
          const weekId = timestampToWeekId(primaryPoint.x);
          const dayId = byDayId ? timestampToDayId(primaryPoint.x) : undefined;

          if (renderCustomTooltip) {
            return renderCustomTooltip({
              chartInstance: this,
              primaryPoint,
              secondaryPoint,
              percentChange,
              weekId,
              dayId,
              metricType
            });
          }

          return ReactDOMServer.renderToStaticMarkup(
            <HoverTooltip
              theme={theme}
              title={dayId ? moment(dayId, 'YYYYMMDD').format('MMM D') : `Week ${weekId % 100}`}
              primaryColor={(primaryPoint.color as string) || theme.colors.primary}
              primaryMetric={`${formatMetric(primaryPoint.y, metricType, {
                decimalPlaces: 2,
                showFullValue: metricType === METRICTYPE.PERCENT,
                showNegative: true
              })}`}
              primaryMetricLabel={
                dayId
                  ? ''
                  : getFormattedMetricDateLabel(
                    weekId,
                    dayId,
                    primaryPoint.series.index === 2 && comparisonPeriod === ComparisonPeriod.PRIOR_YEAR // the secondary series will always have index 2, subtract a year if comparing against prior year
                  )
              }
              change={
                percentChange !== undefined
                  ? `${formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 })}`
                  : undefined
              } // get rid of negative sign if there is one
              rawChange={percentChange}
              secondaryMetric={
                secondaryPoint
                  ? `${formatMetric(secondaryPoint.y, metricType, {
                    decimalPlaces: 2,
                    showFullValue: metricType === METRICTYPE.PERCENT,
                    showNegative: true
                  })}`
                  : ''
              }
              secondaryMetricLabel={getFormattedMetricDateLabel(
                weekId,
                dayId,
                comparisonPeriod === ComparisonPeriod.PRIOR_YEAR
              )}
              secondaryColor={secondaryPoint ? (secondaryPoint.color as string) : theme.colors.primaryLight}
              tooltipWrapperStyle={{ width: '172px' }}
            />
          );
        }
      },
      xAxis: {
        tickLength: 0,
        minTickInterval: 24 * 3600 * 1000,
        lineColor: 'transparent',
        labels: {
          style: textStyles,
          rotation: 0,
          formatter() {
            return Highcharts.dateFormat('%b %e', +this.value);
          }
        }
      },
      yAxis: Array.from({ length: 3 }) // 3 for forecast, primary, and secondary
        .map(() => ({
          gridLineWidth: 0,
          title: {
            text: ''
          },
          labels: {
            formatter() {
              return `${formatMetric(this.value, metricType, {
                decimalPlaces: 2,
                showFullValue: false,
                showNegative: true
              })}`;
            },
            style: textStyles
          },
          min: Math.min(0, chartMin)
        }))
        .map((axis, index) => _merge(axis, _get(chartOptionsOverride, ['yAxis', index], {}))),
      series
    } as Highcharts.Options,
    preview ? previewOptions : {},
    chartOptionsOverride || {}
  );

  const noSeriesData = series.reduce((acc, s) => acc && !s?.data?.length, true);

  return noSeriesData && !!NoDataPlaceholder ? (
    NoDataPlaceholder
  ) : (
    <HighchartsReact highcharts={Highcharts} options={options} />
  );
};

export default SplineChart;
